// src/pages/general/AddListeningQuestionsToGeneral.jsx
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Headphones,
  Save,
  ArrowRight,
  Loader2,
  Image as ImageIcon,
  X,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { generalQuestionsAPI } from "../../services/generalService";
import TranslationField from "../../components/TranslationField";

/* ------------------------------------------------------------------ */
/* Rich text editor for the question text (Bold / Italic / Underline / Highlight) */
/* ------------------------------------------------------------------ */
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-cyan-100 text-cyan-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function QuestionTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[90px] px-3 py-2 focus:outline-none text-right",
        dir: "rtl",
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-cyan-500">
      <div className="flex gap-1 border-b border-gray-200 p-1 bg-gray-50 rounded-t-xl">
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function AddListeningQuestionsToGeneral() {
  const navigate = useNavigate();
  const { skillId, audioId } = useParams();

  const emptyQ = {
    question_text: "",
    options: ["", "", "", ""],
    correct_answer: "",
    explanation: "",
    english_explanation: "",
    translation: "",
    points: 1,
  };
  const [form, setForm] = useState(emptyQ);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const updateOption = (i, val) => {
    const opts = [...form.options];
    opts[i] = val;
    setForm({ ...form, options: opts });
  };

  const handleQuestionTextChange = (html) => {
    setForm((prev) => ({ ...prev, question_text: html }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // question_text now comes from the rich text editor as HTML,
    // so an empty value looks like "<p></p>" instead of ""
    const isQuestionTextEmpty =
      !form.question_text ||
      form.question_text.replace(/<[^>]*>/g, "").trim() === "";
    if (isQuestionTextEmpty) {
      setError("نص السؤال مطلوب");
      return;
    }
    if (
      !form.correct_answer.trim() ||
      !form.options.includes(form.correct_answer)
    ) {
      setError("حدد الإجابة الصحيحة");
      return;
    }
    try {
      setLoading(true);
      setError("");

      const formData = new FormData();
      formData.append("question_text", form.question_text);
      formData.append("options", JSON.stringify(form.options.filter((o) => o.trim())));
      formData.append("correct_answer", form.correct_answer);
      formData.append("explanation", form.explanation);
      formData.append("english_explanation", form.english_explanation);
      formData.append("translation", form.translation || "");
      formData.append("points", form.points);
      if (imageFile) {
        formData.append("question_image", imageFile);
      }

      await generalQuestionsAPI.createListeningQuestion(audioId, formData);
      setSuccess("تم إضافة السؤال!");
      setForm(emptyQ);
      setImageFile(null);
      setImagePreview(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/dashboard/general/skills/${skillId}`)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
          <Headphones className="w-5 h-5 text-cyan-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          إضافة سؤال Listening
        </h1>
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm">
            {success}
          </div>
        )}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            نص السؤال <span className="text-red-500">*</span>
          </label>
          <QuestionTextEditor
            value={form.question_text}
            onChange={handleQuestionTextChange}
          />
          <TranslationField
            label="ترجمة نص السؤال"
            value={form.translation}
            onChange={(v) => setForm((prev) => ({ ...prev, translation: v }))}
          />
        </div>

        {/* Question Image */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            صورة السؤال (اختياري)
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded-xl border border-gray-200"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl py-6 cursor-pointer hover:border-cyan-400 transition-colors">
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">اضغط لرفع صورة</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الخيارات
          </label>
          {form.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                name="correct"
                checked={form.correct_answer === opt && opt.trim() !== ""}
                onChange={() =>
                  opt.trim() && setForm({ ...form, correct_answer: opt })
                }
                className="accent-cyan-600"
              />
              <div className="flex-1 relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">
                  {String.fromCharCode(65 + i)}
                </span>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    updateOption(i, e.target.value);
                    if (form.correct_answer === opt)
                      setForm((p) => ({
                        ...p,
                        correct_answer: e.target.value,
                      }));
                  }}
                  className={`w-full pr-8 pl-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-right ${
                    form.correct_answer === opt && opt.trim()
                      ? "border-cyan-400 bg-cyan-50"
                      : "border-gray-200"
                  }`}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الشرح بالعربي (اختياري)
          </label>
          <textarea
            value={form.explanation}
            onChange={(e) => setForm({ ...form, explanation: e.target.value })}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-right resize-none"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-left">
            Explanation in English (optional)
          </label>
          <textarea
            value={form.english_explanation}
            onChange={(e) =>
              setForm({ ...form, english_explanation: e.target.value })
            }
            rows={2}
            dir="ltr"
            placeholder="Explain the correct answer..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-left resize-none"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/general/skills/${skillId}`)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            رجوع
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            حفظ السؤال
          </button>
        </div>
      </form>
    </div>
  );
}